import * as Sentry from '@sentry/react'
import axios from 'axios'
import Swal, { SweetAlertResult } from 'sweetalert2'
import { ApiErrorData, buildErrorMessage } from './BuildErrorMessage'
import { TFunction } from '_metronic/i18n/Metronici18n'
import { sessaoEncerrandoPorExpiracao } from '../../app/modules/auth/core/sessaoExpirada'

/**
 * Alerta para o usuário digitar a senha
 * @returns Senha - string | undefined
 */
export const showFillCertificado = async (t: TFunction): Promise<string | undefined> => {
  const { isConfirmed, value } = await Swal.fire({
    title: t('certificado.title'),
    input: 'password',
    icon: 'info',
    showCancelButton: true,
    confirmButtonText: t('common.send'),
    cancelButtonText: t('common.cancel'),
    reverseButtons: true,
    buttonsStyling: false,
    customClass: {
      popup: 'bg-body',
      confirmButton: 'btn btn-primary',
      cancelButton: 'btn btn-secondary',
    },
    inputAttributes: {
      maxlength: '16',
      autocapitalize: 'off',
      autocorrect: 'off',
    },

    preConfirm: (senha) => {
      if (!senha || senha.length < 1) {
        Swal.showValidationMessage(t('certificado.password'))
        return false
      }
      return senha
    },
  })
  return isConfirmed ? value : undefined
}

/**
 * Pede o código do autenticador para confirmar uma operação sensível. Aceita tanto os 6
 * dígitos do aplicativo quanto um código de recuperação, que o servidor normaliza.
 * @returns o código informado, ou undefined se o usuário cancelar
 */
export const showFillCodigo2fa = async (options: {
  title: string
  text: string
}): Promise<string | undefined> => {
  const { isConfirmed, value } = await Swal.fire({
    title: options.title,
    text: options.text,
    input: 'text',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Confirmar',
    cancelButtonText: 'Cancelar',
    reverseButtons: true,
    buttonsStyling: false,
    customClass: {
      popup: 'bg-body',
      confirmButton: 'btn btn-primary',
      cancelButton: 'btn btn-secondary',
    },
    inputAttributes: {
      maxlength: '11',
      inputmode: 'numeric',
      autocomplete: 'one-time-code',
      autocapitalize: 'off',
      autocorrect: 'off',
    },
    preConfirm: (codigo) => {
      if (!codigo || codigo.trim().length < 6) {
        Swal.showValidationMessage('Informe o código do autenticador')
        return false
      }
      return codigo.trim()
    },
  })
  return isConfirmed ? value : undefined
}

/**
 * Cria alerta de confirmação para o usuário
 * @param options Options para alerta - title, text, icon, confirmButton
 * @returns boolean
 */
export const showConfirmAlert = async (options?: {
  title?: string
  text?: string
  icon?: 'success' | 'error' | 'warning' | 'info' | 'question'
  confirmButtonText?: string
  cancelButtonText?: string
  /** Força o botão de confirmação em vermelho (ação destrutiva com texto customizado). */
  danger?: boolean
  /** Quando cancelar é a ação segura: destaca e foca o cancelar (Enter não confirma por reflexo). */
  emphasizeCancel?: boolean
  html?: string
  /** Abre com o foco em cancelar, para um Enter a mais não confirmar a ação destrutiva. */
  focusCancel?: boolean
}): Promise<boolean> => {
  const isDanger = options?.danger ?? !options?.confirmButtonText
  const destaqueConfirmar = isDanger ? 'btn-danger' : 'btn-primary'
  const result = await Swal.fire({
    title: options?.title || 'Você tem certeza?',
    text: options?.text || 'Essa ação não pode ser desfeita!',
    icon: options?.icon || 'warning',
    html: options?.html || '',
    showCancelButton: true,
    confirmButtonText: options?.confirmButtonText || 'Sim, excluir!',
    cancelButtonText: options?.cancelButtonText || 'Não',
    reverseButtons: true,
    focusCancel: !!(options?.focusCancel || options?.emphasizeCancel),
    customClass: {
      popup: 'bg-body',
      confirmButton: `btn ${options?.emphasizeCancel ? 'btn-secondary' : destaqueConfirmar}`,
      cancelButton: `btn ${options?.emphasizeCancel ? 'btn-primary' : 'btn-secondary'}`,
    },
    buttonsStyling: false,
    didOpen: () => Swal.hideLoading(),
  })

  return result.isConfirmed
}

export type AlertChoice<T extends string> = {
  value: T
  text: string
  /** Classe de cor do botão (`btn-<variant>`). Padrão: `primary` na 1ª escolha, `secondary` na 2ª. */
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'light'
}

type ChoiceAlertOptions<T extends string> = {
  title: string
  text?: string
  icon?: 'success' | 'error' | 'warning' | 'info' | 'question'
  /** As duas opções. A 1ª é a ação padrão: botão principal, à direita, com o foco. */
  choices: [AlertChoice<T>, AlertChoice<T>]
  /** Mostra o terceiro botão, "Cancelar". */
  showCancelButton?: boolean
  /** Texto do botão Cancelar (padrão: "Cancelar"). Só vale com `showCancelButton`. */
  cancelButtonText?: string
}

/**
 * Alerta com duas opções e, opcionalmente, um terceiro botão "Cancelar".
 * - Com `showCancelButton`: cancelar (botão, Esc ou clique fora) devolve `undefined`.
 * - Sem ele: fechar sem escolher (Esc, clique fora) devolve a 1ª opção, a padrão.
 * @example
 * const destino = await showChoiceAlert({
 *   title: 'Formulário salvo',
 *   icon: 'success',
 *   choices: [
 *     { value: 'editar', text: 'Continuar editando' },
 *     { value: 'lista', text: 'Voltar para a listagem' },
 *   ],
 * })
 * @returns o `value` da opção escolhida, ou `undefined` se cancelou
 */
export const showChoiceAlert = async <T extends string>(
  options: ChoiceAlertOptions<T>
): Promise<T | undefined> => {
  const [principal, segunda] = options.choices

  // Swal tem três botões fixos: confirm (1ª opção), deny (2ª) e cancel (Cancelar).
  const result = await Swal.fire({
    title: options.title,
    text: options.text || '',
    icon: options.icon,
    confirmButtonText: principal.text,
    showDenyButton: true,
    denyButtonText: segunda.text,
    showCancelButton: !!options.showCancelButton,
    cancelButtonText: options.cancelButtonText || 'Cancelar',
    reverseButtons: true,
    customClass: {
      popup: 'bg-body',
      confirmButton: `btn btn-${principal.variant ?? 'primary'}`,
      denyButton: `btn btn-${segunda.variant ?? 'secondary'}`,
      cancelButton: 'btn btn-secondary',
    },
    buttonsStyling: false,
    didOpen: () => Swal.hideLoading(),
  })

  if (result.isConfirmed) return principal.value
  if (result.isDenied) return segunda.value
  return options.showCancelButton ? undefined : principal.value
}

/** Extrai o payload de erro do backend (`{data: {errors, message}}`) de um erro do axios. */
export const getApiErrorData = (error: unknown): ApiErrorData | undefined =>
  axios.isAxiosError(error) ? (error.response?.data as { data?: ApiErrorData })?.data : undefined

/** `message` do envelope de erro do backend; `undefined` quando o erro não traz envelope. */
export const getApiErrorMessage = (error: unknown): string | undefined =>
  getApiErrorData(error)?.message

/**
 * Mensagem do envelope do backend; senão o `message` solto no corpo da resposta; senão a do
 * próprio `Error`. Para quem precisa de algum texto mesmo sem envelope (rede, proxy, timeout).
 */
export const getErrorMessage = (error: unknown): string | undefined => {
  const doEnvelope = getApiErrorMessage(error)
  if (doEnvelope != null) return doEnvelope
  if (axios.isAxiosError(error)) {
    const solto = (error.response?.data as { message?: unknown } | undefined)?.message
    if (solto != null) return String(solto)
  }
  return error instanceof Error ? error.message : undefined
}

/** `true` quando o axios não chegou a receber resposta do servidor (rede, timeout, CORS). */
const isNetworkError = (error: unknown): boolean => axios.isAxiosError(error) && !error.response

export const showError = (error: unknown) => {
  if (isNetworkError(error)) {
    return showAlert({
      title: 'Sem conexão',
      text: 'Não foi possível se conectar ao servidor. Verifique sua internet e tente novamente.',
      icon: 'error',
    })
  }

  const status = axios.isAxiosError(error) ? error.response?.status : undefined
  const apiErrorData = getApiErrorData(error)

  const mensagemDoBackend = apiErrorData
    ? buildErrorMessage(apiErrorData)
    : error instanceof Error
    ? error.message
    : undefined

  // Sessão vencida: o AuthInit já está levando ao login com o aviso; um alerta por requisição
  // que falhou junto só cobriria a tela de login.
  if (status === 401 && !apiErrorData && sessaoEncerrandoPorExpiracao()) {
    return Promise.resolve({ isConfirmed: false, isDenied: false, isDismissed: true })
  }

  // 403 de @PreAuthorize chega com o "Access Denied" do Spring (back antigo) ou sem envelope
  // (negado ainda nos filtros); regra com mensagem própria (ex.: assinatura) mantém o texto.
  if (status === 403) {
    const mensagemDaRegra = apiErrorData?.message?.trim()
    const semMensagemPropria = !mensagemDaRegra || /^access denied$/i.test(mensagemDaRegra)
    return showAlert({
      title: 'Acesso negado',
      html: semMensagemPropria
        ? 'Seu perfil não tem permissão para realizar esta ação.'
        : `<p>${mensagemDaRegra}</p>`,
      icon: 'warning',
    })
  }

  if (status !== 401 && (!apiErrorData || (status !== undefined && status >= 500))) {
    Sentry.captureException(error)
  }

  return showAlert({
    title: status === 401 && !mensagemDoBackend ? 'Sessão expirada' : 'Erro!',
    html:
      mensagemDoBackend ||
      (status === 401
        ? 'Sua sessão expirou. Faça login novamente para continuar.'
        : 'Um erro inesperado aconteceu.'),
    icon: status === 401 ? 'warning' : 'error',
  })
}

/**
 * Aplica erros de validação por campo (`{data: {errors: {campo: [...]}}}`) num Formik via
 * `setFieldError`, para os campos informados em `fields`.
 * @returns `true` se houver algum outro erro além dos campos tratados (campo não mapeado no
 * formulário, ou erro sem esse formato) — nesse caso, mostre também `showError(error)`.
 */
export const applyApiFieldErrors = (
  error: unknown,
  setFieldError: (field: string, message: string) => void,
  fields: string[]
): boolean => {
  const errors = getApiErrorData(error)?.errors
  if (!errors) return true

  let temOutroErro = false
  for (const campo in errors) {
    const mensagem = errors[campo]
    if (fields.includes(campo)) {
      setFieldError(campo, Array.isArray(mensagem) ? mensagem[0] : mensagem)
    } else {
      temOutroErro = true
      // eslint-disable-next-line no-console
      console.warn(
        `[applyApiFieldErrors] Erro de validação para o campo "${campo}" não está mapeado em ` +
          `nenhum campo do formulário (fields: [${fields.join(', ')}]). Se for um campo do ` +
          'formulário, adicione-o à lista; caso contrário, ignore.'
      )
    }
  }

  return temOutroErro
}

export const showAlert = (options: {
  title: string
  text?: string
  icon?: 'success' | 'error' | 'warning' | 'info' | 'question'
  html?: string
  showLoading?: boolean
}): Promise<SweetAlertResult<any>> => {
  return Swal.fire({
    title: options.title,
    text: options.text ? options.text : '',
    html: options.html ? options.html : '',
    icon: options.icon,
    buttonsStyling: false,
    confirmButtonText: 'Ok',
    customClass: {
      popup: 'bg-body',
      confirmButton: 'btn btn-primary',
    },
    didOpen: () => (options.showLoading ? Swal.showLoading() : Swal.hideLoading()),
    ...(options.showLoading && {
      showConfirmButton: false,
      allowOutsideClick: false,
    }),
  })
}

export const showAlertLoading = () =>
  showAlert({
    title: 'Aguarde...',
    text: 'Carregando...',
    icon: 'info',
    showLoading: true,
  })
